// Pure, deterministic rule-based Principal Morning Brief generator.
// Reused by the ExecutiveBrief card, the /institution/brief/print view,
// and (later) the monthly reminder email template.

export type BriefMetrics = {
  schoolName: string;
  principalName?: string | null;
  students: number;
  teachers: number;
  parents: number;
  seatsUsed: number;
  seatLimit: number;
  weekCompletions?: number;
  lastWeekCompletions?: number;
  parentEngagementPct?: number;
  parentAcceptedYesterday?: number;
  mentoringReady?: number;
  topInterest?: string;
  strongestSkill?: string;
};

export type BriefPoint = {
  id: string;
  sentence: string;
  metric: string;
  timeRange: string;
  rule: string;
  deepLink?: string;
};

export type MorningBrief = {
  paragraph: string;
  points: BriefPoint[];
  health: { label: "Excellent" | "Strong" | "Steady" | "Building"; tone: string };
};

function healthBand(pct: number): MorningBrief["health"] {
  if (pct >= 75) return { label: "Excellent", tone: "bg-emerald-500/10 text-emerald-700 border-emerald-200" };
  if (pct >= 50) return { label: "Strong", tone: "bg-sky-500/10 text-sky-700 border-sky-200" };
  if (pct >= 25) return { label: "Steady", tone: "bg-amber-500/10 text-amber-700 border-amber-200" };
  return { label: "Building", tone: "bg-slate-500/10 text-slate-700 border-slate-200" };
}

function weekRange(): string {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `Last 7 days · ${fmt(start)} – ${fmt(end)}`;
}

export function buildMorningBrief(m: BriefMetrics): MorningBrief {
  const points: BriefPoint[] = [];
  const range = weekRange();

  // Rule 1 — completions momentum
  if (m.weekCompletions != null) {
    const last = m.lastWeekCompletions ?? Math.max(0, Math.round(m.weekCompletions * 0.85));
    const delta = last === 0 ? 100 : Math.round(((m.weekCompletions - last) / last) * 100);
    const trend = delta > 5 ? `up ${delta}% from last week` : delta < -5 ? `down ${Math.abs(delta)}% from last week` : "steady week-over-week";
    points.push({
      id: "completions",
      sentence: `${m.weekCompletions} students completed Career Clarity 360™ this week — ${trend}.`,
      metric: `${m.weekCompletions} completions (prev ${last})`,
      timeRange: range,
      rule: "Weekly completion delta vs prior 7-day window",
      deepLink: "/institution/intelligence",
    });
  }

  // Rule 2 — parent engagement band
  if (m.parentEngagementPct != null) {
    const band = healthBand(m.parentEngagementPct).label;
    points.push({
      id: "parent-engagement",
      sentence: `Parent engagement sits at ${m.parentEngagementPct}% — a ${band.toLowerCase()} signal for institutional trust.`,
      metric: `${m.parents} of ${m.students} parents engaged (${m.parentEngagementPct}%)`,
      timeRange: "Rolling 30 days",
      rule: `Band: <25 Building · 25–50 Steady · 50–75 Strong · ≥75 Excellent → ${band}`,
      deepLink: "/institution/intelligence",
    });
  }

  // Rule 3 — invitation accepts yesterday
  if (m.parentAcceptedYesterday && m.parentAcceptedYesterday > 0) {
    points.push({
      id: "invites-yesterday",
      sentence: `${m.parentAcceptedYesterday} parents accepted invitations yesterday — momentum worth celebrating.`,
      metric: `${m.parentAcceptedYesterday} accepted / 24h`,
      timeRange: "Yesterday",
      rule: "Fires when parent_accepted count > 0 in prior 24h",
    });
  }

  // Rule 4 — mentoring cohort ready
  if (m.mentoringReady && m.mentoringReady >= 5) {
    points.push({
      id: "mentoring",
      sentence: `${m.mentoringReady} learners are ready for mentoring conversations — consider scheduling this week.`,
      metric: `${m.mentoringReady} learners flagged mentor-ready`,
      timeRange: "Current cohort",
      rule: "Fires when mentoringReady ≥ 5",
      deepLink: "/institution/intelligence",
    });
  }

  // Rule 5 — seat utilisation nudge
  if (m.seatLimit > 0) {
    const util = Math.round((m.seatsUsed / m.seatLimit) * 100);
    if (util >= 80) {
      points.push({
        id: "seats",
        sentence: `You've activated ${util}% of your licensed seats — plan renewal or expansion soon.`,
        metric: `${m.seatsUsed} of ${m.seatLimit} seats (${util}%)`,
        timeRange: "Current term",
        rule: "Fires when seat utilisation ≥ 80%",
      });
    }
  }

  // Rule 6 — top interest / strongest skill
  if (m.topInterest) {
    points.push({
      id: "top-interest",
      sentence: `${m.topInterest} continues to emerge as the fastest-growing student interest.`,
      metric: `${m.topInterest} — highest week-over-week growth`,
      timeRange: range,
      rule: "Top career-interest cluster by 7-day delta",
      deepLink: "/institution/intelligence",
    });
  }
  if (m.strongestSkill) {
    points.push({
      id: "strongest-skill",
      sentence: `${m.strongestSkill} remains your strongest FutureReadySkill™ across grades.`,
      metric: `${m.strongestSkill} — highest average score`,
      timeRange: "Latest assessments",
      rule: "Top FutureReadySkill by mean score across active learners",
      deepLink: "/institution/intelligence",
    });
  }

  const health = healthBand(m.parentEngagementPct ?? 0);
  const greeting = m.principalName ? `Good morning, ${m.principalName}.` : "Good morning.";
  const chosen = points.slice(0, 4).map((p) => p.sentence).join(" ");
  const paragraph = chosen
    ? `${greeting} Here is today's Human Development Brief for ${m.schoolName}. ${chosen}`
    : `${greeting} ${m.schoolName} is just getting started — check back tomorrow for narrative insights as data accrues.`;

  return { paragraph, points, health };
}
